/* eslint-disable indent */

import TimeUtils from "util/time-utils";
import Constants from "const/constants";
import { reaction, autorun } from "mobx";
import { observer } from "mobx-react";
import { getEnv } from "mobx-state-tree";
import ContextMenu from "pages/common/components/context-menu/contextmenu";
import Icon from "pages/common/components/decorations/icons/icon";
import { Spinner } from "pages/common/components/state/spinners";
import ToolTip from "pages/common/components/tooltip/tooltip";
import Utils from "pages/common/lib/grabyo-util";

import {
    sectionSettingsOpened,
    openSectionSettings,
    closeSectionSettings,
    closeTranscriberSidebar
} from "pages/editor/lib/editor";
import {
    DND_SECTION_HANDLE_LEFT,
    DND_SECTION_HANDLE_RIGHT
} from "pages/editor/lib/timeline";
import * as React from "react";

import {
    SectionUIBadgeContainer,
    SectionDuration,
    SectionContextMenuButton,
    CaptionsState,
    SectionContainer,
    SectionClickCatcher,
    SectionVisualizationDisplay,
    BufferTooltip
} from "./section.styles";
import SectionHandle from "./sectionHandle";
import SectionVisualization from "./sectionVisualization";
import Transitions from "./transitions";

export const EL_BUFFER_SIZE = 25;

/**
 * @typedef {SectionProps}
 * @property {SectionStore} section
 * @property {EditorUIStore} ui
 */

/**
 * @summary - Section view
 * @param {SectionProps} props
 * @returns {React.Element}
 */
const Section = (props) => {
    const { section, ui } = props;
    const clickRef = React.useRef();

    const [showContextMenu, setShowContextMenu] = React.useState(false);
    const [UIVisible, setSectionUIVisible] = React.useState(false);
    const [showCaptionStatus, setShowCaptionStatus] = React.useState(false);
    const [contextMenuPosition, setContextMenuPosition] = React.useState(null);
    const [templateTitleString, setTemplateTitleString] = React.useState(``);

    const durationFormat = TimeUtils.determineTimeFormat(
        section.durationSeconds
    );

    const formattedSectionDuration = TimeUtils.secondsToInputTimeFormat(
        section.durationSeconds,
        true,
        durationFormat
    );

    const onContextMenu = (evt) => {
        evt.preventDefault();
        setContextMenuPosition({
            contextMenuX: evt.pageX,
            contextMenuY: evt.pageY
        });
        setShowContextMenu(true);
    };

    const closeContextMenu = () => {
        setShowContextMenu(false);
        setContextMenuPosition(null);
    };

    const onClick = (evt) => {
        evt.stopPropagation();
        if (ui.activeTransitionId) {
            ui.clearActiveTransition();
        }
        ui.setActiveSection(section.id);
        closeContextMenu();
    };

    const onDuplicateClick = (evt) => {
        evt.stopPropagation();
        section.duplicate();
    };

    const onRemoveClick = () => {
        if (sectionSettingsOpened()) {
            closeSectionSettings();
            closeTranscriberSidebar();
        }

        if (section.id === ui.activeSectionId) {
            ui.clearActiveSection();
        }

        section.remove();
    };

    const onExtractTextClick = async () => {
        const { captionService } = getEnv(ui);
        const trackQuery = captionService._tracksRef(section.asset.captionId);
        const trackId = await trackQuery.get().then(({ docs }) => docs[0].id);

        const { id } = await ui.composition.addAsset({
            captionId: section.asset.captionId,
            trackId,
            type: Constants.MEDIA_TYPE.CAPTION,
            status: Constants.ASSET_UPLOAD_STATUS.COMPLETE
        });

        ui.composition.addSection(
            {
                duration: section.duration,
                startTime: section.startTime,
                playbackStartTime: section.playbackStartTime,
                assetId: id,
                id: Utils.generateGrabyoId(11)
            },
            `captions`
        );

        openSectionSettings(section.id);
    };

    const onDoubleClick = () => openSectionSettings(section.id);

    const onToggleMuteClick = () =>
        section.muted ? section.unmute() : section.mute();

    const showSectionUI = section.selected && UIVisible;

    const canExtractText =
        !!section.asset.captionId &&
        !!ui.composition.captionLayer &&
        section.asset.type !== Constants.MEDIA_TYPE.CAPTION;

    /**
     * Effect for showing/hiding the section UI if the section shrinks below a certain size
     */
    React.useEffect(() => {
        const checkUI = () => {
            if (section.width - EL_BUFFER_SIZE < 60) {
                setSectionUIVisible(false);
                setShowCaptionStatus(false);
            } else {
                setSectionUIVisible(true);
                setShowCaptionStatus(true);
            }
        };
        checkUI();
        return reaction(
            () => section.width,
            () => checkUI()
        );
    }, []);

    /**
     * Define template section text preview
     */
    React.useEffect(() => {
        if (section.asset?.type === Constants.MEDIA_TYPE.TEMPLATE) {
            return autorun(() => {
                let string = ``;
                const field =
                    section.templateFields.get(`title`) ||
                    section.templateFields.get(`subtitle`);
                if (!field) {
                    // If no field, find the first `content` field and use that as the templatTitleString
                    section.templateFields.forEach((field) => {
                        field.forEach((propValue, propName) => {
                            if (
                                propName === `content` &&
                                !templateTitleString
                            ) {
                                string = propValue.get(`0`).value;
                            }
                        });
                    });
                } else {
                    string = field.get(`content`).get(`0`).value;
                }
                setTemplateTitleString(string);
            });
        }
    }, []);

    const hasBuffer = ui.composition.getAsset(section.assetId)?.hasBuffer;

    return (
        <SectionContainer
            active={section.selected}
            isCaption={section.asset?.type === Constants.MEDIA_TYPE.CAPTION}
        >
            <SectionClickCatcher
                ref={clickRef}
                onContextMenu={onContextMenu}
                onClick={onClick}
                onDoubleClick={onDoubleClick}
            >
                <Transitions section={section} ui={ui} />
                {section.asset?.type !== Constants.MEDIA_TYPE.TEMPLATE ? (
                    <SectionVisualization section={section} />
                ) : (
                    <SectionVisualizationDisplay>
                        {templateTitleString}
                    </SectionVisualizationDisplay>
                )}

                <SectionUIBadgeContainer>
                    {showCaptionStatus &&
                        section.asset?.transcriptionStatus ===
                            Constants.ASSET_UPLOAD_STATUS.PROCESSING && (
                            <CaptionsState activeSection={section.selected}>
                                <Icon name="cc-card" />
                                <Spinner />
                            </CaptionsState>
                        )}

                    {showCaptionStatus &&
                        section.asset?.transcriptionStatus ===
                            Constants.ASSET_UPLOAD_STATUS.SUCCESSFUL && (
                            <CaptionsState activeSection={section.selected}>
                                <Icon name="cc-card" />
                            </CaptionsState>
                        )}

                    {showCaptionStatus &&
                        section.asset?.transcriptionStatus ===
                            Constants.ASSET_UPLOAD_STATUS.FAILED && (
                            <CaptionsState activeSection={section.selected}>
                                <Icon name="cc-card" />
                                <Icon name="fail" />
                            </CaptionsState>
                        )}

                    {showSectionUI && (
                        <SectionDuration
                            ccEnabled={
                                showCaptionStatus &&
                                section.asset?.transcriptionStatus !== null
                            }
                        >
                            {formattedSectionDuration}s
                        </SectionDuration>
                    )}

                    {hasBuffer && showSectionUI && (
                        <ToolTip innerHtml="Asset contains buffer" distance={5}>
                            <BufferTooltip>
                                <Icon name="info" />
                            </BufferTooltip>
                        </ToolTip>
                    )}
                </SectionUIBadgeContainer>

                {showSectionUI && (
                    <SectionContextMenuButton
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onContextMenu(e);
                        }}
                    >
                        <Icon name="small-menu" />
                    </SectionContextMenuButton>
                )}

                <SectionHandle
                    side={DND_SECTION_HANDLE_LEFT}
                    ui={ui}
                    section={section}
                />

                <SectionHandle
                    side={DND_SECTION_HANDLE_RIGHT}
                    ui={ui}
                    section={section}
                />

                {showContextMenu &&
                    (() => {
                        {
                            const items = [
                                {
                                    id: `mute`,
                                    onClick: onToggleMuteClick,
                                    text: section.muted ? `Unmute` : `Mute`
                                },
                                {
                                    id: `delete`,
                                    onClick: onRemoveClick,
                                    text: `Delete`
                                },
                                {
                                    id: `settings`,
                                    onClick: onDoubleClick,
                                    text: `Settings`
                                },
                                {
                                    id: `duplicate`,
                                    onClick: onDuplicateClick,
                                    text: `Duplicate`
                                }
                            ];

                            if (canExtractText) {
                                items.push({
                                    id: `extract`,
                                    onClick: onExtractTextClick,
                                    text: `Extract Captions`
                                });
                            }

                            return (
                                <ContextMenu
                                    positionX={contextMenuPosition.contextMenuX}
                                    positionY={contextMenuPosition.contextMenuY}
                                    items={items}
                                    onClose={closeContextMenu}
                                    parentRef={clickRef}
                                />
                            );
                        }
                    })()}
            </SectionClickCatcher>
        </SectionContainer>
    );
};

export default observer(Section);
